/**
 * The full thread — the whole fridge door, grouped by day.
 *
 * Everyone in the house reads and writes here, kids included: this router is not
 * behind the adult gate, and it is deliberately the one place where a nine-year-old
 * and a parent have exactly the same powers, minus the one that matters
 * (an adult can take any note down; a kid only their own).
 *
 * The page never invents state: a failed read says so and offers a retry, an empty
 * board says so and invites the first note, and a failed post hands the words back.
 */
import { useMemo } from 'react';
import { useIdentity } from '../../lib/identity';
import type { Message } from '../../shared/types';
import Composer from './Composer';
import MessageItem, { PendingItem } from './MessageItem';
import { ErrorState, LoadingMessages, dayHeading, dayKey, useNow } from './shared';
import { useBoard } from './useBoard';

/** The Worker caps a page at 200. Ask for exactly that, and say so if we hit it. */
const LIMIT = 200;

interface DayGroup {
  key: string;
  heading: string;
  messages: Message[];
}

export default function MessagesPage() {
  const me = useIdentity();
  const now = useNow();
  const { messages, pending, total, loading, error, reload, post, edit, remove } = useBoard(LIMIT);

  // Newest first from the API; grouping preserves that order inside each day.
  const groups = useMemo<DayGroup[]>(() => {
    const out: DayGroup[] = [];
    for (const m of messages) {
      const key = dayKey(m.created_at);
      const last = out[out.length - 1];
      if (last && last.key === key) last.messages.push(m);
      else out.push({ key, heading: dayHeading(m.created_at, now), messages: [m] });
    }
    return out;
    // `now` only changes the heading wording (Today / Yesterday), which is exactly
    // when these need recomputing anyway.
  }, [messages, now]);

  const capped = messages.length >= LIMIT;

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 sm:px-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-ink">Family board</h1>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">
          One thread, the whole house. Notes, reminders, thank-yous, bad jokes.
        </p>
      </header>

      <section className="rounded-card border border-border bg-surface p-5 sm:p-6">
        <h2 className="sr-only">Write a message</h2>
        <Composer me={me} onPost={post} rows={3} placeholder="Say something to the family…" />
      </section>

      <section className="rounded-card border border-border bg-surface p-5 sm:p-6">
        <h2 className="sr-only">Messages</h2>

        {loading && <LoadingMessages rows={4} />}

        {/* Never render a failed read as an all-clear, empty board. */}
        {!loading && error && <ErrorState message={error} onRetry={reload} />}

        {!loading && !error && messages.length === 0 && pending.length === 0 && (
          <div className="py-6 text-center">
            <p className="font-display text-xl font-semibold text-ink">Nothing here yet.</p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink-soft">
              The board starts empty. Write the first note above — everyone in the house will see
              it the next time they open the app.
            </p>
          </div>
        )}

        {!loading && !error && (messages.length > 0 || pending.length > 0) && (
          <div className="space-y-8">
            {pending.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-ink-faint uppercase">
                  Sending
                </h3>
                <ul className="mt-3 space-y-6">
                  {pending.map((p) => (
                    <PendingItem key={p.tempId} me={me} body={p.body} />
                  ))}
                </ul>
              </div>
            )}

            {groups.map((group) => (
              <div key={group.key}>
                <h3 className="text-xs font-semibold tracking-wide text-ink-faint uppercase">
                  {group.heading}
                </h3>
                <ul className="mt-3 space-y-6">
                  {group.messages.map((m) => (
                    <MessageItem
                      key={m.id}
                      message={m}
                      me={me}
                      now={now}
                      onEdit={edit}
                      onDelete={remove}
                    />
                  ))}
                </ul>
              </div>
            ))}

            {capped && (
              <p className="border-t border-border pt-4 text-center text-xs text-ink-faint">
                {/* The total comes from the API's COUNT. If that call failed we don't
                    know it, so we say only what we can see. */}
                {total != null && total > messages.length
                  ? `Showing the ${LIMIT} most recent of ${total} notes.`
                  : `Showing the ${LIMIT} most recent notes.`}
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
